// src/components/InstantChat.js
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import useSEO from '../hooks/useSEO';
import { trackEvent } from '../utils/analytics';
import { 
  MessageCircle, 
  Send, 
  Bot, 
  User, 
  UserPlus, 
  Sparkles,
  Heart,
  Shield,
  Clock,
  Users,
  ArrowRight,
  CheckCircle,
  X
} from 'lucide-react';
import Icon from './Icon';
import Footer from './Footer';
// Note: No longer importing saveAnonymousMessage to avoid duplicate storage

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

function InstantChat() {
  const navigate = useNavigate();

  // SEO optimization
  useSEO({
    title: '3 Anonyma + 12 Credits Gratis - Your Therapist | 15 Chattar Totalt',
    description: 'Börja med 3 anonyma chattar, sedan 12 credits gratis när du skapar konto. Totalt 15 chattar gratis! Därefter köper du credits när du behöver dem.',
    keywords: '15 gratis chattar, 3 anonyma chattar, AI-stöd, mental hälsa hjälp, AI-terapeut, engångsbetalning, inga månadsavgifter',
    image: 'https://www.yourtherapist.se/logo512.png',
    url: 'https://www.yourtherapist.se/instant-chat',
    type: 'website'
  });

  const [messages, setMessages] = useState([
    {
      id: 1,
      type: 'bot',
      content: 'Hej! Jag är ditt AI-stöd och här för att hjälpa dig. De första 15 chattarna är helt gratis så att du kan testa tjänsten. Dina meddelanden kan aldrig ses av någon annan än dig - allt är helt privat och konfidentiellt. Du kan dela vad som helst med mig - jag lyssnar utan att döma och ger stöd dygnet runt. Vad kan jag hjälpa dig med idag?',
      timestamp: new Date().toISOString().slice(0, -1) // Use consistent timestamp format
    }
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showSignupPrompt, setShowSignupPrompt] = useState(false);
  const [messageCount, setMessageCount] = useState(1);
  const inputRef = useRef(null);
  const messagesContainerRef = useRef(null);

  // Clear anonymous session on component mount for fresh start
  useEffect(() => {
    const clearPreviousSession = async () => {
      const existingSessionId = sessionStorage.getItem('anonymous_session_id');
      if (existingSessionId) {
        try {
          console.log('🧹 Clearing previous anonymous session for fresh start...');
          const response = await fetch(`${API_BASE}/api/anonymous-chat/clear-session`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ session_id: existingSessionId })
          });
          
          if (response.ok) {
            const data = await response.json();
            if (data.cleared) {
              console.log(`✅ Cleared session ${existingSessionId} (had ${data.previous_message_count} messages)`);
            } else {
              console.log(`ℹ️ Session ${existingSessionId} was already empty`);
            }
          }
        } catch (error) {
          console.log('⚠️ Could not clear previous session:', error);
          // Non-critical error, continue with fresh session
        }
        
        // Remove the old session ID to force creation of new one
        sessionStorage.removeItem('anonymous_session_id');
        
        // Also clear any leftover anonymous messages from previous sessions
        sessionStorage.removeItem('instantChatMessages');
        sessionStorage.removeItem('pendingAnamnesisMessages');
        console.log('🧹 Also cleared leftover anonymous messages from sessionStorage');
      }
    };
    
    clearPreviousSession();
  }, []); // Empty dependency array - only run on mount

  // Auto-scroll to bottom only when content overflows
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (container) {
      // Check if content is overflowing (scrollable)
      const isOverflowing = container.scrollHeight > container.clientHeight;
      if (isOverflowing) {
        // Only scroll if user is already near the bottom (within 100px)
        const isNearBottom = container.scrollTop + container.clientHeight >= container.scrollHeight - 100;
        if (isNearBottom) {
          container.scrollTop = container.scrollHeight;
        }
      }
    }
  }, [messages]);

  useEffect(() => {
    // Show signup prompt after 3 anonymous chattar (6 messages total including AI responses)
    if (messageCount >= 6 && !showSignupPrompt) {
      setShowSignupPrompt(true);
    }
  }, [messageCount, showSignupPrompt]);

  const handleSendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;

    // Track the send message event
    trackEvent('Instant Chat Message Sent', { 
      location: 'instant-chat',
      message_count: messageCount + 1,
      message_length: inputMessage.trim().length
    });

    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: inputMessage.trim(),
      timestamp: new Date().toISOString().slice(0, -1) // Use consistent timestamp format
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);
    setMessageCount(prev => prev + 1);

    try {
      // Note: We don't save to localStorage here to avoid duplicates
      // Messages will be saved to sessionStorage when user creates account
      
      // Call the anonymous chat API
      // Note: session_id will be null for the first message (creating new session)
      // since we cleared the old session_id on component mount
      const response = await fetch(`${API_BASE}/api/anonymous-chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: userMessage.content,
          session_id: sessionStorage.getItem('anonymous_session_id') // null for first message = new session
        })
      });

      if (!response.ok) {
        throw new Error('Failed to get response');
      }

      const data = await response.json();
      
      // Store session ID for continuity
      sessionStorage.setItem('anonymous_session_id', data.session_id);

      const botResponse = {
        id: Date.now() + 1,
        type: 'bot',
        content: data.response,
        timestamp: new Date().toISOString().slice(0, -1) // Use consistent timestamp format
      };

      // Note: We don't save to localStorage here to avoid duplicates
      // Messages will be saved to sessionStorage when user creates account

      setMessages(prev => [...prev, botResponse]);
      setMessageCount(prev => prev + 1);

    } catch (error) {
      console.error('Error sending message:', error);
      
      // Show error message if AI is not available
      const errorResponse = {
        id: Date.now() + 1,
        type: 'bot',
        content: 'Jag har just nu problem med att ansluta till AI-systemet. Försök igen om en stund eller skapa ett konto för att få tillgång till en mer stabil anslutning.',
        timestamp: new Date().toISOString().slice(0, -1), // Use consistent timestamp format
        isError: true
      };

      setMessages(prev => [...prev, errorResponse]);
      setMessageCount(prev => prev + 1);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleCreateAccount = () => {
    // Track the click event
    trackEvent('From chat to signup Button Click', { 
      location: 'instant-chat-modal',
      trigger: 'signup-prompt',
      message_count: messageCount
    });
    
    // Check if AI is currently processing a response
    if (isLoading) {
      alert('Vänta medan AI:n svarar på ditt senaste meddelande, sedan kan du skapa konto för att spara hela konversationen.');
      return;
    }
    
    // Save conversation to session storage so it can be restored after signup
    // Convert 'bot' type to 'assistant' for backend compatibility
    // Skip the initial greeting message (id: 1) as it doesn't have a user message pair
    const conversationMessages = messages.filter(msg => msg.id !== 1);
    const convertedMessages = conversationMessages.map(msg => ({
      ...msg,
      type: msg.type === 'bot' ? 'assistant' : msg.type
    }));
    
    console.log(`💾 Saving ${convertedMessages.length} messages to sessionStorage for account creation`);
    console.log('Messages being saved:', convertedMessages.map(m => `${m.type}: ${m.content.substring(0, 50)}...`));
    
    // Store in sessionStorage (for chat restoration after signup)
    sessionStorage.setItem('instantChatMessages', JSON.stringify(convertedMessages));
    
    // Note: We don't store in localStorage here to avoid duplicates
    // The messages will be stored properly during the conversion process
    
    navigate('/express-signup');
  };

  const SignupPromptModal = () => {
    if (!showSignupPrompt) return null;

    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl">
          <button 
            onClick={() => setShowSignupPrompt(false)}
            className="float-right text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
          
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-brand-primary rounded-full flex items-center justify-center mx-auto mb-4">
              <Sparkles className="w-8 h-8 text-white" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">
              Du har använt dina 3 anonyma chattar!
            </h3>
            <p className="text-gray-600">
              Skapa ett konto och få 12 credits gratis för att fortsätta chatta. Totalt 15 chattar gratis! Inga månadsavgifter eller bindningstid.
            </p>
          </div>

          <div className="space-y-4 mb-6">
            <div className="flex items-start space-x-3">
              <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <CheckCircle className="w-4 h-4 text-green-600" />
              </div>
              <div>
                <p className="font-medium text-gray-900">Spara alla dina konversationer</p>
                <p className="text-sm text-gray-600">Fortsätt där du slutade när som helst</p>
              </div>
            </div>
            
            <div className="flex items-start space-x-3">
              <div className="w-6 h-6 bg-brand-primary/10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <Users className="w-4 h-4 text-brand-primary" />
              </div>
              <div>
                <p className="font-medium text-gray-900">Förslag på legitimerade terapeuter</p>
                <p className="text-sm text-gray-600">Helt frivilligt - bara om du vill</p>
              </div>
            </div>
            
            <div className="flex items-start space-x-3">
              <div className="w-6 h-6 bg-brand-secondary/10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <Sparkles className="w-4 h-4 text-brand-secondary" />
              </div>
              <div>
                <p className="font-medium text-gray-900">Personliga insikter och framsteg</p>
                <p className="text-sm text-gray-600">AI analyserar dina mönster över tid</p>
              </div>
            </div>
          </div>

          <div className="bg-brand-primary/10 border border-brand-primary/20 rounded-lg p-4 mb-6">
            <div className="flex items-center space-x-2 mb-2">
              <Heart className="w-5 h-5 text-brand-primary" />
              <p className="font-medium text-brand-primary">Flexibel betalning</p>
            </div>
            <p className="text-sm text-brand-primary/80">
              Köp bara det du behöver - inga månadsavgifter eller bindningstid. Credits förfaller aldrig och du bestämmer helt själv när du vill köpa fler.
            </p>
          </div>

          <div className="flex justify-center">
            <button
              onClick={handleCreateAccount}
              disabled={isLoading}
              className={`w-full px-4 py-3 rounded-lg transition-all font-medium flex items-center justify-center space-x-2 ${
                isLoading 
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                  : 'bg-brand-primary text-white hover:bg-brand-primary-dark'
              }`}
            >
              <UserPlus className="w-4 h-4" />
              <span>{isLoading ? 'Väntar på AI-svar...' : 'Skapa konto & få 12 gratis credits'}</span>
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen w-full">
      {/* Chat Container */}
      <div className="max-w-4xl mx-auto px-4 py-6">
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden border border-gray-200">
          {/* Benefits Banner */}
          <div className="bg-brand-primary text-white px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Bot className="w-6 h-6" />
                <div>
                  <p className="font-semibold">Professionellt AI-stöd</p>
                  <p className="text-sm opacity-90">Konfidentiell • Säker • Alltid tillgänglig</p>
                </div>
              </div>
              <div className="hidden md:flex items-center space-x-4 text-sm">
                <div className="flex items-center space-x-1">
                  <Shield className="w-4 h-4" />
                  <span>GDPR-säker</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Clock className="w-4 h-4" />
                  <span>24/7</span>
                </div>
              </div>
            </div>
          </div>

          {/* Messages */}
          <div 
            ref={messagesContainerRef}
            className="p-6 space-y-4"
          >
            {messages.map((message) => (
              <div
                key={message.id}
                className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-xs lg:max-w-md px-4 py-3 rounded-2xl ${
                    message.type === 'user'
                      ? 'bg-brand-primary text-white'
                      : 'bg-gray-100 text-gray-900'
                  }`}
                >
                  <div className="flex items-start space-x-2">
                    {message.type === 'bot' && (
                      <Bot className="w-5 h-5 text-brand-primary flex-shrink-0 mt-0.5" />
                    )}
                    <p className="text-sm leading-relaxed">{message.content}</p>
                    {message.type === 'user' && (
                      <User className="w-5 h-5 text-white/80 flex-shrink-0 mt-0.5" />
                    )}
                  </div>
                </div>
              </div>
            ))}
            
            {isLoading && (
              <div className="flex justify-start">
                <div className="max-w-xs lg:max-w-md px-4 py-3 rounded-2xl bg-gray-100">
                  <div className="flex items-center space-x-2">
                    <Bot className="w-5 h-5 text-brand-primary" />
                    <div className="flex space-x-1">
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Input */}
          <div className="border-t border-gray-200 p-4">
            <div className="flex space-x-3 items-center">
              <textarea
                ref={inputRef}
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Skriv ditt meddelande här..."
                rows="1"
                className="flex-1 px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-pink-500 focus:border-transparent resize-none h-[50px]"
              />
              <button
                onClick={handleSendMessage}
                disabled={!inputMessage.trim() || isLoading}
                className="px-4 py-3 bg-brand-primary text-white rounded-xl hover:bg-brand-primary-dark disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center h-[50px] w-[50px] flex-shrink-0"
              >
                <Send className="w-5 h-5" />
              </button>
            </div>
            
            <div className="flex items-center justify-between mt-3 text-xs text-gray-500">
              <p>Tryck Enter för att skicka, Shift+Enter för ny rad</p>
              <p>{messages.length} meddelanden</p>
            </div>
          </div>
        </div>

        {/* Info Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8">
          <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center space-x-2 mb-2">
              <Shield className="w-5 h-5 text-green-600" />
              <h3 className="font-semibold text-gray-900">100% Konfidentiellt</h3>
            </div>
            <p className="text-sm text-gray-600">
              Alla konversationer är krypterade och följer GDPR-regler
            </p>
          </div>
          
          <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center space-x-2 mb-2">
              <Users className="w-5 h-5 text-brand-primary" />
              <h3 className="font-semibold text-gray-900">Mänsklig Support</h3>
            </div>
            <p className="text-sm text-gray-600">
              Få förslag på terapeuter när du vill - helt frivilligt
            </p>
          </div>
          
          <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center space-x-2 mb-2">
              <Clock className="w-5 h-5 text-brand-secondary" />
              <h3 className="font-semibold text-gray-900">Alltid Tillgänglig</h3>
            </div>
            <p className="text-sm text-gray-600">
              AI-stöd dygnet runt, alla dagar i veckan
            </p>
          </div>
        </div>
      </div>

      <SignupPromptModal />
      
    </div>
  );
}

export default InstantChat;
